```vue
<template> 
  <div class="confirmation-container d-flex align-items-center justify-content-center" 
    :class="{ 'compact-view': asistenciaConfirmada }"> 
    <div class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg" 
      :class="{ 'compact-card': asistenciaConfirmada }" 
      ref="formCardRef"> 
      
      <!-- Full Confirmation View --> 
      <div v-if="asistenciaConfirmada" class="qr-view text-center"> 
        <h3 class="confirmation-thanks">Thank you for confirming your attendance!</h3> 
      </div> 
 
      <!-- Step-by-Step Form --> 
      <div v-else> 
        <div class="form-header"> 
          <h2 ref="tituloAnimado" class="form-title"> 
            Confirm<br><span class="subtitulo">Attendance</span> 
          </h2> 
          <br> 
          <div class="progress-container"> 
            <div 
              class="progress-bar" 
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }" 
            ></div> 
          </div> 
          <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p> 
        </div> 
        <form @submit.prevent="handleSubmit" class="confirmation-form"> 
          
          <!-- Step 1: Name and Phone --> 
          <div v-if="currentStep === 1" class="step-box"> 
            <div class="form-group"> 
              <label for="name" class="form-label">Name</label> 
              <input 
                id="name" 
                v-model.trim="name" 
                type="text" 
                class="form-control" 
                placeholder="Enter your full name" 
                required 
              > 
            </div> 
            <div class="form-group"> 
              <label for="phone" class="form-label">Phone</label> 
              <input 
                id="phone" 
                v-model.trim="phone" 
                type="tel" 
                class="form-control" 
                placeholder="Enter your phone number" 
                required 
                pattern="[0-9]{10}" 
                title="Enter a 10-digit number" 
              > 
            </div> 
          </div> 
 
          <!-- Step 2: Invited by (hidden for XV or YM) --> 
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box"> 
            <div class="form-group"> 
              <label class="form-label">Invited by?</label> 
              <div class="radio-group"> 
                <div class="radio-option"> 
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required> 
                  <label for="novia" class="radio-label">Bride</label> 
                </div> 
                <div class="radio-option"> 
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input"> 
                  <label for="novio" class="radio-label">Groom</label> 
                </div> 
              </div> 
            </div> 
          </div> 
 
          <!-- Final Step: Number of Guests --> 
          <div v-if="currentStep === totalSteps" class="step-box"> 
            <div class="form-group"> 
              <label for="guests" class="form-label">How many guests are attending?</label> 
              <select 
                id="guests" 
                v-model.number="guests" 
                class="form-select" 
                :disabled="loading" 
                required 
              > 
                <option :value="null" disabled>Select an option</option> 
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option> 
              </select> 
              <div v-if="loading" class="loading-indicator">Loading options...</div> 
            </div> 
          </div> 
 
          <!-- Navigation Buttons --> 
          <div class="d-flex justify-content-between mt-3"> 
            <button type="button" class="submit-button" @click="prevStep" v-if="currentStep > 1">Back</button> 
            <button v-if="currentStep < totalSteps" type="button" class="submit-button" @click="nextStep">Next</button> 
            <button v-if="currentStep === totalSteps" type="submit" class="submit-button" :disabled="isSubmitting"> 
              <span v-if="!isSubmitting">Confirm</span> 
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> 
            </button> 
          </div> 
          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div> 
        </form> 
      </div> 
    </div> 
  </div> 
</template> 
 
<script> 
import { ref, onMounted, computed, nextTick } from 'vue'; 
import axios from 'axios'; 
import baseUrl from '@/api'; 
import gsap from 'gsap'; 
import { ScrollTrigger } from 'gsap/ScrollTrigger'; 
 
gsap.registerPlugin(ScrollTrigger); 
 
export default { 
  name: 'AttendanceConfirmation', 
  props: { 
    idInvitado: { type: Number, required: false, default: null }, 
    nombre: { type: String, default: '' }, 
    idFestejado: { type: Number, default: null } 
  }, 
  setup(props) { 
    const name = ref(''); 
    const phone = ref(''); 
    const invitedBy = ref(''); 
    const guests = ref(null); 
    const guestsOptions = ref([1, 2, 3, 4, 5, 6]); 
    const loading = ref(false); 
    const isSubmitting = ref(false); 
    const errorMessage = ref(''); 
    const tituloAnimado = ref(null); 
    const asistenciaConfirmada = ref(false); 
    const currentStep = ref(1); 
    const formCardRef = ref(null); 
 
    const isXvEvent = computed(() => { 
      const eventName = props.nombre?.toLowerCase(); 
      return ['xv', 'ym'].includes(eventName); 
    }); 
 
    const totalSteps = computed(() => isXvEvent.value ? 2 : 3); 
 
    const animateStep = () => { 
      nextTick(() => { 
        const box = formCardRef.value?.querySelector('.step-box'); 
        if (!box) return; 
        gsap.fromTo( 
          box, 
          { y: 50, opacity: 0, transformPerspective: 300, rotationX: -10 }, 
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', rotationX: 0 } 
        ); 
      }); 
    }; 
 
    const nextStep = () => { 
      if (currentStep.value === 1) { 
        if (!name.value.trim() || !phone.value.trim()) { 
          errorMessage.value = 'Please enter your name and phone number.'; 
          return; 
        } 
      } 
      if (currentStep.value === 2 && !isXvEvent.value) { 
        if (!invitedBy.value) { 
          errorMessage.value = 'Please select who invited you.'; 
          return; 
        } 
      } 
      if (currentStep.value === totalSteps.value) { 
        if (guests.value == null || Number.isNaN(Number(guests.value))) { 
          errorMessage.value = 'Please select how many people will be attending.'; 
          return; 
        } 
      } 
      errorMessage.value = ''; 
      if (currentStep.value < totalSteps.value) { 
        currentStep.value++; 
        animateStep(); 
      } 
    }; 
 
    const prevStep = () => { 
      if (currentStep.value > 1) { 
        currentStep.value--; 
        animateStep(); 
      } 
    }; 
 
    const fetchGuestLimit = async () => { 
      if (!props.idInvitado) return; 
      loading.value = true; 
      try { 
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`); 
        const numPases = Number(response.data.num_pase); 
        guestsOptions.value = numPases > 0 
          ? Array.from({ length: numPases }, (_, i) => i + 1) 
          : [1, 2]; 
      } catch (error) { 
        console.error('', error); 
      } finally { 
        loading.value = false; 
      } 
    }; 
 
    const handleSubmit = async () => { 
      if (guests.value == null || Number.isNaN(Number(guests.value))) { 
        errorMessage.value = 'Please select how many people will be attending.'; 
        return; 
      } 
 
      isSubmitting.value = true; 
      errorMessage.value = ''; 
      try { 
        const formData = { 
          nombre_asistencia: name.value.trim(), 
          telefono_invitado: phone.value.trim(), 
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value, 
          asistira: 'si', 
          id_festejada: props.idFestejado, 
          id_invitado: props.idInvitado, 
          num_pases: Number(guests.value ?? 0), 
        }; 
        await axios.post(`${baseUrl}/asistencia`, formData); 
        asistenciaConfirmada.value = true; 
      } catch (error) { 
        console.error('Error confirming attendance:', error); 
        errorMessage.value = 'There was an error confirming your attendance. Please try again.'; 
      } finally { 
        isSubmitting.value = false; 
      } 
    }; 
 
    onMounted(() => { 
      fetchGuestLimit(); 
      animateStep(); 
      gsap.fromTo( 
        tituloAnimado.value, 
        { y: -150, opacity: 0, scale: 1.2 }, 
        { 
          y: 0, 
          opacity: 1, 
          scale: 1, 
          duration: 1.2, 
          ease: 'bounce.out', 
          scrollTrigger: { 
            trigger: tituloAnimado.value, 
            start: 'top 80%', 
            toggleActions: 'restart none none reset' 
          } 
        } 
      ); 
    }); 
 
    return { 
      name, phone, invitedBy, guests, guestsOptions, 
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada, 
      currentStep, nextStep, prevStep, handleSubmit, 
      totalSteps, formCardRef, tituloAnimado 
    }; 
  } 
}; 
</script>
```
<style scoped>
/* ESTILOS DE LA ALERTA PERSONALIZADA (TINTO / CHIC) */
.custom-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 110vh;
  background: rgba(15, 5, 8, 0.65);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  margin-bottom: 50px;;
}

.custom-alert-card {
  background: #9aa5b8;
  border: 1px solid rgba(212, 175, 55, 0.4);
  border-radius: 16px;
  padding: 1.8rem 1.5rem;
  max-width: 280px;
  width: 80%;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  animation: popIn 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.alert-icon {
  font-size: 1.8rem;
  margin-bottom: 0.4rem;
}

.alert-title {
  font-family: 'Playfair Display', serif;
  color: #575654;
  font-size: 1.35rem;
  margin-bottom: 0.8rem;
  letter-spacing: 0.5px;
}

.alert-message {
  font-family: 'Karla', sans-serif;
  color: #fffffe;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.4rem;
}

.alert-message strong {
  color: #ffd700;
  font-weight: 600;
}

.alert-button {
background: linear-gradient(
  135deg,
  #f0c9d3 0%,
  #f5f7fa 25%,
  #ffffff 50%,
  #f0c9d3 75%,
  #f0c9d3 100%
);  color: #2b060a;
  font-family: 'Karla', sans-serif;
  font-weight: 700;
  border: none;
  border-radius: 25px;
  padding: 0.55rem 1.8rem;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.alert-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(191, 149, 63, 0.4);
}

@keyframes popIn {
  0% { opacity: 0; transform: scale(0.85) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

/* Transición Vue Fade */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* CONTENEDOR CONTENIDO */
.confirmation-container {
  width: 100%;
  padding: 1rem 0.5rem;
  box-sizing: border-box;
}

/* TARJETA MARCO AZUL */
/* TARJETA MARCO AZUL */
.confirmation-card {
  width: 100%;
  margin-bottom: 50px;
  max-width: 340px !important; /* Ajuste ligero para dar aire a la pantalla */
  min-height: 650px;
  background: url('@/assets/Diseño sin título (78).png') no-repeat center center;
  background-size: 100% 100%;
  /* AUMENTO RADICAL DEL PADDING LATERAL: de 45px a 75px */
  /* Esto empuja todo el contenido hacia el centro */
  padding: 195px 75px 45px !important; 
  border-radius: 16px;
  box-sizing: border-box;
  position: relative; /* Importante para asegurar el posicionamiento interno */
}

.card-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.form-wrapper {
  width: 100%;
}

/* ENCABEZADOS Y TÍTULOS */
.form-header {
  margin-bottom: 0.8rem;
  margin-top:-50px;
}

.form-title {
  font-family: 'ludovica', 'Great Vibes', cursive;
  font-size: 36px !important;
  color: #5999d2;
  text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0 0 2px;
  line-height: 1.1;
  text-align: center;
}

.rsvp-deadline {
  font-family: 'Playfair Display', serif;
  font-size: 10.5px;
  color: rgba(81, 67, 45, 0.95);
  margin: 0 0 8px;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color:#5999d2;
  font-size: 10px;
  margin-top: 3px;
}

/* BARRA DE PROGRESO COMPACTA */
.progress-container {
  height: 4px;
  background-color: rgba(110, 101, 64, 0.3);
  border-radius: 4px;
  overflow: hidden;
  width: 75%;
  margin: 0 auto;
}

.progress-bar {
  height: 100%;
  background-color: #89844c;
  transition: width 0.4s ease;
}

/* CAMPOS DE FORMULARIO - REDUCIDOS EN ANCHO */
.form-group {
  margin-bottom: 0.65rem;
  text-align: left;
}

.form-label {
  display: block;
  margin-bottom: 0.2rem;
  padding-right:30px !important ;
  font-family: 'Karla', sans-serif;
  font-size: 19px !important;
  font-weight: 600;
  color: #2c2626;
}

.form-control,
.form-select {
  width: 100%;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0.4rem;
  background-color: rgba(255, 255, 255, 0.95);
  color: #333333;
  box-sizing: border-box;
}

.form-control:focus,
.form-select:focus {
  border-color: #5999d2;
  box-shadow: 0 0 0 0.18rem rgba(191, 149, 63, 0.3);
  outline: none;
}

/* OPCIONES SÍ/NO */
.radio-group {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  padding: 0.2rem 0;
  color: #2a2929;
  font-family: 'Karla', sans-serif;
  font-size: 13px;
}

/* BOTONES CON TEXTURA DORADA */
.submit-button {
  width: 100%;
  padding: 0.5rem 0.65rem;
  font-family: 'Playfair Display', serif;
  font-size: 1.15rem;
  font-weight: 800;
background-image: linear-gradient(
  135deg,
  rgba(255, 255, 255, 0),
  rgba(220, 230, 242, 0.098),
  rgba(180, 195, 215, 0.014)
), url('@/assets/dorado.png');

background-size: cover;  background-size: cover;
  background-position: center;
  background-color: #bf953f;
  color: #1d1c1c !important;
  -webkit-text-fill-color: #000000 !important;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

.submit-button.secondary {
  background: rgba(255, 255, 255, 0.85) !important;
  color: #333333 !important;
  -webkit-text-fill-color: #333333 !important;
}

/* MENSAJE DE CONFIRMACIÓN FINAL */
.qr-header {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  color: #1d1c1c;
  margin-bottom: 0.4rem;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: rgba(37, 36, 36, 0.9);
  font-size: 0.85rem;
}

.alert {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 0.4rem;
}

@media (max-width: 576px) {
  .confirmation-card {
    padding: 175px 36px 36px;
    min-height: 580px;
  }

  .form-title {
  }
}
</style>